import React, { useState, useEffect } from 'react';
import { XIcon, CheckIcon } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../lib/api';
import { useInvalidateNotes, type NoteRecord } from '../hooks/useNotes';
import { FileUpload } from './FileUpload';
import { createClient } from '../../lib/supabase/client';
import {
  buildNoteFilePath,
  uploadNoteFile,
} from '../../lib/storage/note-files';

interface Message {
  id: string;
  text: string;
  timestamp: string;
  isOutgoing: boolean;
  media_url?: string;
  media_type?: string;
}

interface AddNoteModalProps {
  message: Message | null;
  clientId?: string; // For clients
  appointmentId?: string; // For linking note to appointment
  onClose: () => void;
  onSuccess?: () => void;
}

export function AddNoteModal({
  message,
  clientId,
  appointmentId,
  onClose,
  onSuccess
}: AddNoteModalProps) {
  const { session } = useAuth();
  const invalidateNotes = clientId ? useInvalidateNotes(clientId) : () => {};

  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [tags, setTags] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<string>('');

  // Pre-fill with message text if provided
  useEffect(() => {
    if (message) {
      setTitle('Note from WhatsApp');
      setNote(message.text);

      // Add media-specific tag if message has media
      if (message.media_type) {
        setTags(`whatsapp-message, ${message.media_type}`);
      } else {
        setTags('whatsapp-message');
      }
    }
  }, [message]);

  // Auto-download and prepare media file if message has media
  useEffect(() => {
    console.log('Media download effect triggered:', {
      hasMediaUrl: !!message?.media_url,
      hasMediaType: !!message?.media_type,
      media_url: message?.media_url,
      media_type: message?.media_type
    });

    if (!message?.media_url || !message?.media_type) {
      console.log('No media to download - skipping');
      return;
    }

    const downloadMediaFile = async () => {
      try {
        console.log('Starting media download...');
        setUploadProgress('Downloading media from WhatsApp...');

        // Build the full URL - handle both proxy paths and full URLs
        let mediaUrl = message.media_url;
        if (mediaUrl.startsWith('/api/')) {
          // Relative proxy URL - use the proxy service
          const baseUrl = window.location.origin.replace(':5173', ':3001');
          mediaUrl = `${baseUrl}${message.media_url}`;
        }
        console.log('Fetching media from:', mediaUrl);

        const response = await fetch(mediaUrl, {
          headers: session?.access_token ? {
            'Authorization': `Bearer ${session.access_token}`,
          } : {},
        });

        if (!response.ok) {
          throw new Error(`Failed to download media: ${response.status} ${response.statusText}`);
        }

        const blob = await response.blob();

        // Determine file extension and MIME type based on media type and blob
        let extension = 'bin';
        let mimeType = blob.type || 'application/octet-stream';

        if (message.media_type === 'image') {
          extension = blob.type === 'image/png' ? 'png' : 'jpg';
        } else if (message.media_type === 'video') {
          extension = 'mp4';
        } else if (message.media_type === 'document') {
          // Try to get extension from content type
          if (blob.type === 'application/pdf') extension = 'pdf';
          else if (blob.type.includes('word')) extension = 'docx';
          else extension = 'pdf';
        } else if (message.media_type === 'audio') {
          extension = 'mp3';
        }

        const fileName = `whatsapp-${message.media_type}-${Date.now()}.${extension}`;
        const file = new File([blob], fileName, { type: mimeType });

        console.log('Media downloaded successfully:', {
          fileName,
          size: file.size,
          type: file.type
        });

        setSelectedFiles([file]);
        setUploadProgress('');
        console.log('selectedFiles updated');
      } catch (error) {
        console.error('Failed to download media:', error);
        setError(error instanceof Error ? error.message : 'Failed to download WhatsApp media. You can manually upload it.');
        setUploadProgress('');
      }
    };

    downloadMediaFile();
  }, [message?.media_url, message?.media_type, session?.access_token]);

  const createNoteMutation = useMutation({
    mutationFn: async () => {
      if (!session?.access_token || !session?.user?.id) {
        throw new Error('You must be logged in to save notes');
      }
      if (!clientId) {
        throw new Error('This contact is not a client yet. Please convert them to a client first using the "Convert to Client" button in the chat header.');
      }
      if (!title.trim() || !note.trim()) {
        throw new Error('Title and note content are required');
      }

      const supabase = createClient();
      const fileUrls: string[] = [];
      const fileNames: string[] = [];
      const fileTypes: string[] = [];
      const fileSizes: number[] = [];

      // Upload files if any
      if (selectedFiles.length > 0) {
        setUploadProgress(`Uploading ${selectedFiles.length} file(s)...`);

        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];
          setUploadProgress(`Uploading ${file.name} (${i + 1}/${selectedFiles.length})...`);

          const path = buildNoteFilePath({
            userId: session.user.id,
            clientId,
            originalName: file.name,
          });

          await uploadNoteFile(supabase, path, file);

          fileUrls.push(path);
          fileNames.push(file.name);
          fileTypes.push(file.type);
          fileSizes.push(file.size);
        }
      }

      setUploadProgress('Saving note...');

      const payload = {
        title: title.trim(),
        body: note.trim(),
        tags: tags.split(',').map((tag) => tag.trim()).filter(Boolean),
        file_urls: fileUrls,
        file_names: fileNames,
        file_types: fileTypes,
        file_sizes: fileSizes,
        appointment_id: appointmentId || null,
      };

      return apiRequest<NoteRecord>(
        `/api/clients/${clientId}/notes`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        },
        session.access_token,
      );
    },
    onSuccess: () => {
      invalidateNotes();
      onSuccess?.();
      setUploadProgress('');
      setTimeout(() => onClose(), 1000);
    },
    onError: (err) => {
      setUploadProgress('');
      setError(err instanceof Error ? err.message : 'Failed to save note');
    },
  });

  const handleSave = () => {
    setError(null);
    createNoteMutation.mutate();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-hidden animate-scale-in flex flex-col">
        {/* Fixed Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-semibold text-[#212121]">
            {message ? 'Save Message to Notes' : 'Add Note'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            disabled={createNoteMutation.isPending}
          >
            <XIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-2 rounded-xl">
              {error}
            </div>
          )}
          {message && (
            <div className="p-3 bg-gray-50 rounded-xl">
              <p className="text-sm text-gray-600 mb-1">Original Message:</p>
              <p className="text-sm text-[#212121] italic whitespace-pre-wrap">"{message.text}"</p>
              <p className="text-xs text-gray-500 mt-1">{message.timestamp}</p>
              {message.media_url && message.media_type && (
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-700 font-medium">
                    📎 Media attached: {message.media_type}
                    {selectedFiles.length > 0 ? (
                      <span className="ml-1 text-green-600">✓ Downloaded</span>
                    ) : (
                      <span className="ml-1 text-blue-600">(downloading...)</span>
                    )}
                  </p>
                </div>
              )}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Client feedback on meal plan"
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
              disabled={createNoteMutation.isPending}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Note Content *
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add your note here..."
              rows={5}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all resize-none"
              disabled={createNoteMutation.isPending}
              autoFocus={!message}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Tags (optional)
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="feedback, meal-plan, follow-up"
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
              disabled={createNoteMutation.isPending}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Attachments (optional)
            </label>
            <FileUpload
              onFilesChange={setSelectedFiles}
              maxFiles={5}
              initialFiles={selectedFiles}
            />
          </div>
          {uploadProgress && (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 text-sm px-4 py-2 rounded-xl">
              {uploadProgress}
            </div>
          )}
        </div>

        {/* Fixed Footer */}
        <div className="p-4 border-t border-gray-200 flex gap-2 flex-shrink-0 bg-white">
          <button
            onClick={onClose}
            className="flex-1 bg-gray-100 text-gray-600 font-semibold py-3 rounded-xl hover:bg-gray-200 transition-colors"
            disabled={createNoteMutation.isPending}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!title.trim() || !note.trim() || createNoteMutation.isPending}
            className={`flex-1 font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 ${
              createNoteMutation.isSuccess
                ? 'bg-[#5B8E87] text-white'
                : title.trim() && note.trim() && !createNoteMutation.isPending
                ? 'bg-[#5B8E87] text-white hover:bg-[#4F7A74]'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {createNoteMutation.isSuccess ? (
              <>
                <CheckIcon className="w-5 h-5" />
                Saved!
              </>
            ) : createNoteMutation.isPending ? (
              'Saving...'
            ) : (
              'Save Note'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}